import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, ViewChild, computed, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CARRYOVER_SOURCE } from './carryover-source.token';

/**
 * Draws a history button beside a field and shows the previous report's value
 * while the user hovers it.
 *
 * It reads no report, asks no question and writes nothing. This is a `ui`
 * library, so it cannot inject the report store. A directive on the wrapper
 * provides `CARRYOVER_SOURCE`, which answers the questions and does the write.
 * The vessel report form uses `nspCarryOver`. A wrapper with no source throws
 * NG0201 when it first draws, so a field cannot draw a button that does
 * nothing.
 */
@Component({
  selector: 'nsp-carryover-input-wrapper',
  templateUrl: './carryover-input-wrapper.component.html',
  styleUrls: ['./carryover-input-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FontAwesomeModule, CommonModule, TooltipModule, ButtonModule],
})
export class CarryoverInputWrapper {
  @ViewChild('container', { static: true })
  container?: ElementRef<HTMLElement>;

  #originalPlaceholder = signal<string | undefined>(undefined);

  readonly #source = inject(CARRYOVER_SOURCE);

  /**
   * The value the button writes. It is whatever the control holds, so a select
   * takes an option object and a text box takes text. Undefined means the
   * template has nothing to write, and the button greys.
   */
  carryoverValue = this.#source.value;
  /**
   * Whether the field draws the button at all. The template decides: a field
   * with no `carryOver` entry, and a field that carries only when the form
   * creates a report, both draw nothing.
   */
  showHistoryButton = this.#source.showButton;
  /**
   * Whether a lock stops the button running. A disabled field and a read-only
   * field each keep the button and grey it, so a reader sees the field would
   * carry if the lock were off.
   */
  historyButtonDisabled = this.#source.buttonDisabled;

  historyIcon = faClockRotateLeft;

  suggestionText = computed(() => {
    const value = this.carryoverValue();
    if (value == null) return '';
    if (value instanceof Date) return this.formatDate(value);
    if (typeof value === 'object' && 'name' in value && typeof value.name === 'string') return value.name;
    return String(value);
  });

  hasCarryoverValue = computed(() => this.carryoverValue() != null);

  /** True while the button draws and cannot run, for either of the two reasons. */
  isButtonDisabled = computed(() => this.historyButtonDisabled() || !this.hasCarryoverValue());

  /**
   * The three states a reader needs. 'Nothing to carry' and 'a lock stops it'
   * are different facts about the same greyed button, and a tooltip that said
   * one for both would send a reader to the wrong place.
   */
  tooltipText = computed(() => {
    if (this.historyButtonDisabled()) return 'The field is locked, so this cannot run';
    return this.hasCarryoverValue() ? 'Use previous report value' : 'No previous report value';
  });

  onHoverEnter() {
    const suggestion = this.suggestionText();
    if (!suggestion) return;

    const selectLabel = this.getSelectLabelElement();
    if (selectLabel) {
      selectLabel.setAttribute('data-suggestion', suggestion);
      selectLabel.classList.add('nsp-carryover-preview');
      return;
    }

    const input = this.getInputElement();
    if (!input) return;

    if (this.#originalPlaceholder() === undefined) {
      this.#originalPlaceholder.set(input.placeholder);
    }
    input.placeholder = suggestion;
  }

  onHoverLeave() {
    const selectLabel = this.getSelectLabelElement();
    if (selectLabel) {
      selectLabel.removeAttribute('data-suggestion');
      selectLabel.classList.remove('nsp-carryover-preview');
      return;
    }

    const input = this.getInputElement();
    if (!input) return;

    input.placeholder = this.#originalPlaceholder() ?? '';
  }

  onHistoryClick() {
    // The click handler checks the lock as well as the markup, because a
    // component can call this method directly.
    if (this.isButtonDisabled()) return;

    this.#source.apply();
  }

  private getInputElement(): HTMLInputElement | null {
    return this.container?.nativeElement.querySelector('input') ?? null;
  }

  private getSelectLabelElement(): HTMLElement | null {
    return this.container?.nativeElement.querySelector('.p-select') ?? null;
  }

  private formatDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}
